import { PhoneIcon } from '@/components/layout/icons';
import { formatPhoneAsYouType, regionToFlagEmoji, resolveExplicitPhoneRegion } from '@/lib/phone';

// Example number hinting the `+<country code>` form that drives locale
// detection. Language-neutral (digits only), so it needs no translation.
const DEFAULT_PLACEHOLDER = '+49 151 23456789';

type PhoneInputProps = {
  readonly id?: string;
  readonly value: string;
  readonly placeholder?: string;
  /** Receives the as-you-type formatted display value. */
  readonly onChange: (value: string) => void;
  readonly onBlur?: () => void;
  readonly disabled?: boolean;
  readonly invalid?: boolean;
  readonly 'aria-label'?: string | undefined;
  readonly 'aria-describedby'?: string | undefined;
};

/**
 * Single reusable phone field for every entry point. Formats the number
 * as-you-type (default region `DE`, switching on an explicit `+<cc>`), and
 * shows a flag adornment on the right *only* once the number identifies a
 * country via `+<cc>` — no flag is shown for a bare national number, so the
 * field never presumes a region. Validity and E.164 normalisation live in
 * `@/lib/phone` and are run by the owning form on blur/submit.
 */
export function PhoneInput(props: PhoneInputProps) {
  const { id, value, placeholder = DEFAULT_PLACEHOLDER, onChange, onBlur, disabled = false, invalid = false } = props;
  const ariaDescribedBy = props['aria-describedby'];
  const ariaLabel = props['aria-label'];

  const region = resolveExplicitPhoneRegion(value);
  const flag = region === null ? null : regionToFlagEmoji(region);

  const wrapperClasses = [
    'flex h-control-md w-full items-stretch overflow-hidden rounded-md border bg-surface',
    'transition-colors duration-micro ease-out',
    'focus-within:border-interactive focus-within:shadow-focus',
    invalid ? 'border-danger-500 focus-within:border-danger-500 focus-within:shadow-focus-danger' : 'border-border',
    disabled ? 'opacity-60' : '',
  ]
    .filter((c) => c !== '')
    .join(' ');

  return (
    <div className={wrapperClasses}>
      <span aria-hidden className="flex items-center pl-3 text-text-tertiary">
        <PhoneIcon className="h-4 w-4" />
      </span>
      <input
        id={id}
        type="tel"
        inputMode="tel"
        autoComplete="tel"
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(formatPhoneAsYouType(e.target.value))}
        onBlur={onBlur}
        disabled={disabled}
        aria-invalid={invalid || undefined}
        aria-label={ariaLabel}
        aria-describedby={ariaDescribedBy}
        className="min-w-0 flex-1 bg-transparent py-2 pl-2 pr-3 text-base font-sans text-text-primary focus:outline-none disabled:cursor-not-allowed"
      />
      {flag !== null && (
        <span
          aria-hidden
          className="inline-flex items-center border-l border-border bg-neutral-50 px-3 text-base leading-none"
        >
          {flag}
        </span>
      )}
    </div>
  );
}
